import { useState, useEffect, useRef } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import { Card } from '../components/ui/card';
import api from '../lib/api';

export default function VerifyEmail() {
  const [params] = useSearchParams();
  const token = params.get('token') || '';
  const [status, setStatus] = useState('loading'); // loading | success | error
  const [errorCode, setErrorCode] = useState('');
  const calledRef = useRef(false);

  useEffect(() => {
    if (!token) { setStatus('error'); setErrorCode('no_token'); return; }
    if (calledRef.current) return; // StrictMode double-fire guard
    calledRef.current = true;
    api.post('/organiser/verify-email', { token })
      .then(res => {
        // ok: true (fresh or already_verified) → success
        if (res.data.ok) setStatus('success');
        else { setStatus('error'); setErrorCode(res.data.code || 'unknown'); }
      })
      .catch(err => {
        setStatus('error');
        const data = err.response?.data;
        setErrorCode(data?.code || 'unknown');
      });
  }, [token]);

  const errorMessages = {
    no_token: 'No verification token provided.',
    invalid_token: 'This verification link is invalid. Please check your email or request a new link.',
    expired_token: 'This verification link has expired. Please request a new one from your account page.',
    unknown: 'Verification failed. Please try again or contact support.',
  };

  return (
    <div className="min-h-screen bg-[#F4F6F5] flex items-center justify-center px-4" data-testid="verify-email-page">
      <Card className="w-full max-w-md p-8 bg-white border-[#E1E6E4] rounded-xl text-center space-y-4">
        {status === 'loading' && (
          <>
            <div className="w-8 h-8 border-3 border-[#1B4332] border-t-transparent rounded-full animate-spin mx-auto" />
            <p className="text-sm text-[#455561]">Verifying your email...</p>
          </>
        )}
        {status === 'success' && (
          <>
            <div className="w-14 h-14 bg-[#E8F5E9] rounded-full flex items-center justify-center mx-auto">
              <svg className="w-7 h-7 text-[#2E7D32]" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" /></svg>
            </div>
            <h2 className="text-xl font-bold text-[#1D242B]">Email verified</h2>
            <p className="text-sm text-[#455561]">Your account is now verified. You can now log in.</p>
            <Link to="/login" className="text-sm text-[#1B4332] font-semibold hover:underline">Go to login</Link>
          </>
        )}
        {status === 'error' && (
          <>
            <div className="w-14 h-14 bg-[#FFEBEE] rounded-full flex items-center justify-center mx-auto">
              <svg className="w-7 h-7 text-[#D32F2F]" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" /></svg>
            </div>
            <h2 className="text-xl font-bold text-[#1D242B]">Verification failed</h2>
            <p className="text-sm text-[#455561]">{errorMessages[errorCode] || errorMessages.unknown}</p>
            <Link to="/login" className="text-sm text-[#1B4332] font-semibold hover:underline">Go to login</Link>
          </>
        )}
      </Card>
    </div>
  );
}
